import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

/** Ventana de paginas numeradas alrededor de la actual. */
const WINDOW_RADIUS = 1;

/**
 * Paginador de la consola.
 *
 * El API NO devuelve `totalPages`: se calcula aqui como
 * `Math.ceil(total / pageSize)`. La pagina es 1-based.
 */
@Component({
  selector: 'app-pagination',
  imports: [],
  templateUrl: './pagination.component.html',
  styleUrl: './pagination.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginationComponent {
  /** Total de registros reportado por el API. */
  readonly total = input.required<number>();

  /** Pagina actual, 1-based. */
  readonly page = input.required<number>();

  /** Tamano de pagina solicitado al API. */
  readonly pageSize = input.required<number>();

  /** Emite la nueva pagina (1-based). */
  readonly pageChange = output<number>();

  /** El API no devuelve `totalPages`; se deriva del total y el tamano. */
  readonly totalPages = computed(() => {
    const size = this.pageSize();
    if (!size || size < 1) {
      return 1;
    }

    return Math.max(1, Math.ceil(this.total() / size));
  });

  readonly isFirst = computed(() => this.page() <= 1);
  readonly isLast = computed(() => this.page() >= this.totalPages());

  /** Primer registro visible, 1-based. */
  readonly rangeFrom = computed(() =>
    this.total() === 0 ? 0 : (this.page() - 1) * this.pageSize() + 1,
  );

  /** Ultimo registro visible. */
  readonly rangeTo = computed(() => Math.min(this.page() * this.pageSize(), this.total()));

  /**
   * Paginas a dibujar. `null` representa una elipsis, para no imprimir
   * doscientos botones cuando el catalogo es grande.
   */
  readonly pages = computed<(number | null)[]>(() => {
    const totalPages = this.totalPages();
    const current = this.page();

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const items: (number | null)[] = [1];
    const start = Math.max(2, current - WINDOW_RADIUS);
    const end = Math.min(totalPages - 1, current + WINDOW_RADIUS);

    if (start > 2) {
      items.push(null);
    }

    for (let page = start; page <= end; page++) {
      items.push(page);
    }

    if (end < totalPages - 1) {
      items.push(null);
    }

    items.push(totalPages);

    return items;
  });

  protected goTo(page: number): void {
    if (page === this.page() || page < 1 || page > this.totalPages()) {
      return;
    }

    this.pageChange.emit(page);
  }

  protected previous(): void {
    this.goTo(this.page() - 1);
  }

  protected next(): void {
    this.goTo(this.page() + 1);
  }
}
